import { customElement, query, state } from 'lit/decorators.js'
import { html, css, CSSResult } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { View } from './view'
import { Symbols, inject } from '../di'
import { DeviceListStore } from '../stores'
import { DeviceDownload, isMobile } from './device'

const tick = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`
const clock = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>`
const alert = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`
const mailIcon = html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>`
const linkIcon = html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>`
const downloadIcon = html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`

@customElement('kob-device-null')
export class DeviceNullView extends View {
    @inject(Symbols.stores.deviceList)
    devicesList: DeviceListStore

    @query('kob-device-create')
    deviceCreateDialog

    @query('kob-device-download')
    deviceDownloadDialog: DeviceDownload

    @state()
    copyLabel: string = null

    deviceName = () => this.devicesList.firstDevice?.name

    // Clipboard writes are refused outside a secure context, so say what to do instead
    handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(location.href)
            this.copyLabel = 'Link copied'
        }
        catch {
            this.copyLabel = 'Copy it from the address bar'
        }
    }

    // Browsers without File System Access fall through to a plain KoboRoot.tgz download
    handleDownloadFile = () => this.devicesList.firstDevice.installKoblime()

    handleCreate = () => {
        this.deviceCreateDialog.deviceStore = this.devicesList.newStore()
        this.deviceCreateDialog.dialogOpened = true
    }

    handleInstall = () => {
        this.deviceDownloadDialog.deviceStore = this.devicesList.firstDevice
        this.deviceDownloadDialog.dialogOpened = true
    }

    // Rendered into the light DOM (see View.createRenderRoot), so every rule is scoped
    // to the kob-device-null host to keep it away from the global Bootstrap sheet.
    static styles: CSSResult = css`
        kob-device-null {
            display: block;
            /* kob-main clips its slot at this height, so scroll inside it */
            height: calc(100vh - var(--kop-header-height));
            overflow-y: auto;
            background: var(--kob-surface-100);
            color: var(--kob-ink-800);
            font-family: var(--kob-font);
            line-height: 1.55;
        }
        kob-device-null *,
        kob-device-null *::before,
        kob-device-null *::after { box-sizing: border-box; }
        kob-device-null a { color: var(--kob-link); }
        kob-device-null a:hover { color: var(--kob-link-hover); }

        kob-device-null .setup { max-width: 680px; margin: 0 auto; padding: var(--kob-space-8) var(--kob-space-6); }

        /* ---------- Head ---------- */
        kob-device-null .setup-head { text-align: center; margin-bottom: 40px; }
        kob-device-null .setup-badge {
            display: inline-flex;
            align-items: center;
            gap: var(--kob-space-2);
            font-family: var(--kob-mono);
            font-size: 0.68rem;
            text-transform: uppercase;
            letter-spacing: 0.14em;
            color: var(--kob-ink-500);
            background: var(--kob-surface-0);
            border: 1px solid var(--kob-line-200);
            padding: 6px var(--kob-space-3);
            border-radius: var(--kob-radius-pill);
        }
        kob-device-null .setup-badge .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--kob-orange-500); }
        kob-device-null .setup-head h1 {
            margin: 18px 0 0;
            font-size: 2rem;
            font-weight: 700;
            letter-spacing: -0.02em;
            color: var(--kob-ink-900);
            line-height: 1.15;
        }
        kob-device-null .setup-head p { margin: 10px 0 0; font-size: 1rem; color: var(--kob-ink-500); font-weight: 300; }
        kob-device-null .setup-progress { display: flex; align-items: center; justify-content: center; gap: var(--kob-space-2); margin-top: 20px; }
        kob-device-null .setup-progress .seg { width: 46px; height: 5px; border-radius: var(--kob-radius-pill); background: var(--kob-line-200); }
        kob-device-null .setup-progress .seg.done { background: var(--kob-success); }
        kob-device-null .setup-progress .seg.active { background: var(--kob-grad-warm); }
        kob-device-null .setup-progress .lbl { font-family: var(--kob-mono); font-size: 0.72rem; color: var(--kob-ink-500); margin-left: 6px; }

        /* ---------- Stepper ---------- */
        kob-device-null .stepper { display: flex; flex-direction: column; }
        kob-device-null .step { display: grid; grid-template-columns: 40px 1fr; gap: 20px; }
        kob-device-null .rail { display: flex; flex-direction: column; align-items: center; }
        kob-device-null .node {
            width: 38px;
            height: 38px;
            border-radius: 50%;
            display: grid;
            place-items: center;
            font-weight: 700;
            font-size: 0.95rem;
            flex: 0 0 38px;
        }
        kob-device-null .node.done { background: var(--kob-success); color: var(--kob-surface-0); }
        kob-device-null .node.done svg { width: 18px; height: 18px; }
        kob-device-null .node.active {
            background: var(--kob-surface-0);
            color: var(--kob-orange-500);
            border: 2px solid var(--kob-orange-500);
        }
        kob-device-null .node.todo { background: var(--kob-surface-50); color: var(--kob-ink-500); border: 1px solid var(--kob-line-300); }
        kob-device-null .line { width: 2px; flex: 1; min-height: 20px; background: var(--kob-line-200); margin: 6px 0; }
        kob-device-null .line.done { background: var(--kob-success); }
        kob-device-null .step:last-child .line { display: none; }

        kob-device-null .step-body { padding-bottom: var(--kob-space-5); min-width: 0; }
        kob-device-null .step-title {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 1.15rem;
            font-weight: 700;
            color: var(--kob-ink-900);
            letter-spacing: -0.01em;
            padding-top: 6px;
        }
        kob-device-null .step-done-tag {
            font-family: var(--kob-mono);
            font-size: 0.64rem;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            color: var(--kob-success);
        }
        kob-device-null .step-card {
            margin-top: var(--kob-space-3);
            background: var(--kob-surface-0);
            border: 1px solid var(--kob-line-200);
            border-radius: var(--kob-radius-card);
            padding: 18px 20px;
        }
        kob-device-null .step-card.active { border-color: var(--kob-orange-500); border-left-width: 3px; }
        kob-device-null .step.todo .step-title { color: var(--kob-ink-500); }
        kob-device-null .step.todo .step-card { background: transparent; border-style: dashed; }
        kob-device-null .step-card p { margin: 0; font-size: 0.92rem; color: var(--kob-ink-800); line-height: 1.55; }
        kob-device-null .step-card p + p { margin-top: 10px; }
        kob-device-null .step.todo .step-card p { color: var(--kob-ink-500); }
        kob-device-null .step-card strong { color: var(--kob-ink-900); font-weight: 600; }

        kob-device-null .dev-chip {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: var(--kob-space-2) var(--kob-space-3);
            background: var(--kob-surface-50);
            border: 1px solid var(--kob-line-200);
            border-radius: var(--kob-radius-pill);
        }
        kob-device-null .dev-chip .g {
            width: 26px;
            height: 34px;
            border-radius: var(--kob-radius-input);
            background: linear-gradient(160deg, var(--kob-ink-800), var(--kob-ink-900));
            position: relative;
            flex: 0 0 26px;
        }
        kob-device-null .dev-chip .g::after {
            content: "";
            position: absolute;
            inset: 3px 3px 6px;
            border-radius: 2px;
            background: rgba(255, 255, 255, 0.08);
        }
        kob-device-null .dev-chip .nm { font-weight: 600; font-size: 0.9rem; color: var(--kob-ink-900); }
        kob-device-null .dev-chip .md {
            font-family: var(--kob-mono);
            font-size: 0.64rem;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: var(--kob-ink-500);
            margin-left: 6px;
        }

        kob-device-null .step-actions { margin-top: var(--kob-space-4); display: flex; align-items: center; gap: var(--kob-space-3); flex-wrap: wrap; }
        kob-device-null .step-hint { display: flex; align-items: center; gap: 6px; margin-top: var(--kob-space-3); font-size: 0.78rem; color: var(--kob-ink-500); }
        kob-device-null .step-hint svg { width: 13px; height: 13px; flex: 0 0 13px; }
        kob-device-null .step-warning { color: var(--kob-danger); }

        /* ---------- Buttons (canonical set from STYLE.html) ---------- */
        kob-device-null .btn {
            font-family: var(--kob-font);
            font-size: 0.95rem;
            font-weight: 500;
            line-height: 1;
            padding: 12px 24px;
            border-radius: var(--kob-radius-pill);
            border: 1px solid transparent;
            background: transparent;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: var(--kob-space-2);
            text-decoration: none;
            transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        kob-device-null .btn .ico { width: 14px; height: 14px; display: inline-block; }
        kob-device-null .btn.btn-primary {
            background: var(--kob-orange-500);
            color: var(--kob-surface-0);
            border-color: var(--kob-orange-500);
        }
        kob-device-null .btn.btn-primary:hover { background: #e87010; border-color: #e87010; color: var(--kob-surface-0); }
        kob-device-null .btn.btn-ghost { background: transparent; color: var(--kob-ink-500); }
        kob-device-null .btn.btn-ghost:hover { color: var(--kob-ink-900); background: var(--kob-surface-0); }
        kob-device-null .btn.btn-sm { padding: 8px 16px; font-size: 0.85rem; }

        /* ---------- Manual copy (browsers that can't write to the Kobo) ---------- */
        kob-device-null .or-divider {
            display: flex;
            align-items: center;
            gap: var(--kob-space-3);
            margin: 18px 0 4px;
            color: var(--kob-ink-500);
            font-family: var(--kob-mono);
            font-size: 0.68rem;
            text-transform: uppercase;
            letter-spacing: 0.12em;
        }
        kob-device-null .or-divider::before,
        kob-device-null .or-divider::after { content: ""; height: 1px; background: var(--kob-line-200); flex: 1; }
        kob-device-null .mini-steps {
            margin: 14px 0 0;
            padding: 0;
            list-style: none;
            counter-reset: ms;
            display: flex;
            flex-direction: column;
            gap: 10px;
        }
        kob-device-null .mini-steps li {
            counter-increment: ms;
            display: grid;
            grid-template-columns: 22px 1fr;
            gap: 10px;
            align-items: baseline;
            font-size: 0.88rem;
            color: var(--kob-ink-800);
        }
        kob-device-null .mini-steps li::before {
            content: counter(ms);
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: var(--kob-surface-50);
            border: 1px solid var(--kob-line-300);
            color: var(--kob-ink-500);
            font-size: 0.72rem;
            font-weight: 600;
            display: grid;
            place-items: center;
        }
        kob-device-null .mini-steps .btn { padding-left: 0; }
        kob-device-null code { font-family: var(--kob-mono); font-size: 0.82rem; color: var(--kob-ink-800); }
        kob-device-null kbd {
            font-family: var(--kob-mono);
            font-size: 0.72rem;
            background: var(--kob-surface-50);
            border: 1px solid var(--kob-line-300);
            border-bottom-width: 2px;
            border-radius: 4px;
            padding: 1px 5px;
            color: var(--kob-ink-800);
        }

        /* ---------- How Koblime works ---------- */
        kob-device-null .how { margin-top: var(--kob-space-7); }
        kob-device-null .how h2 {
            margin: 0 0 var(--kob-space-5);
            text-align: center;
            font-size: 1.15rem;
            font-weight: 700;
            letter-spacing: -0.01em;
            color: var(--kob-ink-900);
        }
        kob-device-null .how-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--kob-space-3); }
        kob-device-null .how-card {
            background: var(--kob-surface-0);
            border: 1px solid var(--kob-line-200);
            border-radius: var(--kob-radius-card);
            padding: var(--kob-space-4) 18px;
        }
        kob-device-null .how-card .ic {
            width: 34px;
            height: 34px;
            border-radius: var(--kob-space-2);
            display: grid;
            place-items: center;
            background: linear-gradient(135deg, rgba(249, 104, 19, 0.1), rgba(251, 176, 64, 0.14));
            color: var(--kob-flame-600);
            margin-bottom: 10px;
        }
        kob-device-null .how-card .ic svg { width: 17px; height: 17px; }
        kob-device-null .how-card h3 { margin: 0 0 var(--kob-space-1); font-size: 0.92rem; font-weight: 600; color: var(--kob-ink-900); }
        kob-device-null .how-card p { margin: 0; font-size: 0.82rem; color: var(--kob-ink-500); line-height: 1.5; }

        kob-device-null .setup-foot {
            text-align: center;
            margin-top: 36px;
            padding-top: var(--kob-space-5);
            border-top: 1px solid var(--kob-line-200);
            font-size: 0.88rem;
            color: var(--kob-ink-500);
        }

        @media (max-width: 540px) {
            kob-device-null .setup { padding: var(--kob-space-6) var(--kob-space-4); }
            kob-device-null .setup-head h1 { font-size: 1.6rem; }
            kob-device-null .how-grid { grid-template-columns: 1fr; }
        }
    `

    render() {
        const added = this.devicesList.hasEntities
        return html`
            ${unsafeHTML(`<style>${DeviceNullView.styles.cssText}</style>`)}
            <kob-device-create></kob-device-create>
            <kob-device-download></kob-device-download>

            <main class="setup" aria-label="Set up your Kobo">
                <div class="setup-head">
                    <span class="setup-badge"><span class="dot"></span>First-time setup</span>
                    <h1>Let's set up your Kobo</h1>
                    <p>One cable, one time. After this, everything happens over Wi-Fi.</p>
                    <div class="setup-progress" aria-label="Step ${added ? 2 : 1} of 3">
                        <span class="seg ${added ? 'done' : 'active'}"></span>
                        <span class="seg ${added ? 'active' : ''}"></span>
                        <span class="seg"></span>
                        <span class="lbl">Step ${added ? 2 : 1} of 3</span>
                    </div>
                </div>

                <div class="stepper">
                    <div class="step ${added ? 'done' : 'active'}">
                        <div class="rail">
                            <div class="node ${added ? 'done' : 'active'}">${added ? tick : '1'}</div>
                            <div class="line ${added ? 'done' : ''}"></div>
                        </div>
                        <div class="step-body">
                            <div class="step-title">
                                Add your Kobo
                                ${added ? html`<span class="step-done-tag">Done</span>` : ''}
                            </div>
                            <div class="step-card ${added ? '' : 'active'}">
                                ${this.renderDeviceStep()}
                            </div>
                        </div>
                    </div>

                    <div class="step ${added ? 'active' : 'todo'}">
                        <div class="rail">
                            <div class="node ${added ? 'active' : 'todo'}">2</div>
                            <div class="line"></div>
                        </div>
                        <div class="step-body">
                            <div class="step-title">Put Koblime on your Kobo</div>
                            <div class="step-card ${added ? 'active' : ''}">
                                ${this.renderInstallStep()}
                            </div>
                        </div>
                    </div>

                    <div class="step todo">
                        <div class="rail">
                            <div class="node todo">3</div>
                            <div class="line"></div>
                        </div>
                        <div class="step-body">
                            <div class="step-title">Add books — then just read</div>
                            <div class="step-card">
                                <p>
                                    Pick books in the <strong>Books</strong> tab and they appear on your Kobo over
                                    Wi-Fi. Highlights and reading progress come back here on their own.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <section class="how" aria-label="How Koblime works">
                    <h2>How Koblime works</h2>
                    <div class="how-grid">
                        <div class="how-card">
                            <div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg></div>
                            <h3>Books go to your Kobo</h3>
                            <p>Add a book in the <strong>Books</strong> tab — it appears on your Kobo.</p>
                        </div>
                        <div class="how-card">
                            <div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></div>
                            <h3>Highlights come back</h3>
                            <p>Notes and highlights show up in the <strong>Highlights</strong> tab.</p>
                        </div>
                        <div class="how-card">
                            <div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
                            <h3>Progress is backed up</h3>
                            <p>Replace your Kobo and pick up where you left off.</p>
                        </div>
                    </div>
                </section>

                <div class="setup-foot">
                    Stuck on a step? <a href="https://docs.kobli.me" target="_blank">Read the setup guide</a> or
                    <a href="https://github.com/koblime/docs/issues" target="_blank">get in touch</a>.
                </div>
            </main>
        `
    }

    renderDeviceStep = () => this.devicesList.hasEntities ?
        html`
            <div class="dev-chip">
                <span class="g" aria-hidden="true"></span>
                <span>
                    <span class="nm">${this.deviceName()}</span>
                    <span class="md">${this.devicesList.firstDevice.typeLabel}</span>
                </span>
            </div>
        ` :
        html`
            <p>Each Kobo you own is backed up separately, so start by telling us about this one.</p>
            <div class="step-actions">
                <button type="button" class="btn btn-primary" @click=${this.handleCreate}>Add your Kobo</button>
            </div>
        `

    renderInstallStep = () => {
        if (!this.devicesList.hasEntities)
            return html`<p>Add your Kobo first, then we'll put Koblime on it.</p>`

        if (this.devicesList.firstDevice.isUnsupported)
            return html`
                <p>
                    <strong>${this.deviceName()}</strong> can't run Koblime — this model's hardware
                    doesn't support it, so it won't sync.
                </p>
                <div class="step-hint step-warning">
                    ${alert}
                    Add a different Kobo to carry on with setup.
                </div>
            `

        if (isMobile()) return this.renderMobileStep()
        if (!this.devicesList.firstDevice.canDirectInstall) return this.renderManualStep()

        return html`
            <p>
                Plug <strong>${this.deviceName()}</strong> into this computer and tap
                <strong>Connect</strong> on its screen. Then use the button below.
            </p>
            <div class="step-actions">
                <button type="button" class="btn btn-primary" @click=${this.handleInstall}>
                    ${downloadIcon}
                    Set up ${this.deviceName()}
                </button>
            </div>
            <div class="step-hint">
                ${clock}
                Takes about a minute — choose the KOBOeReader drive when asked, then unplug.
            </div>
        `
    }

    renderMobileStep = () => html`
        <p>
            <strong>This step needs a computer.</strong> Your Kobo connects with a USB cable —
            everything after that is wireless.
        </p>
        <div class="step-actions">
            <a class="btn btn-primary" href="mailto:?subject=Set%20up%20my%20Kobo&body=${encodeURIComponent(location.href)}">
                ${mailIcon}
                Email me a link
            </a>
            <button type="button" class="btn btn-ghost btn-sm" @click=${this.handleCopyLink}>
                ${this.copyLabel ?? 'Copy link'}
            </button>
        </div>
    `

    renderManualStep = () => html`
        <p>
            <strong>This browser can't copy files to your Kobo.</strong> Open this page in
            <strong>Chrome</strong> or <strong>Edge</strong> — there it's one click.
        </p>
        <div class="step-actions">
            <button type="button" class="btn btn-primary" @click=${this.handleCopyLink}>
                ${linkIcon}
                ${this.copyLabel ?? 'Copy link for Chrome'}
            </button>
        </div>
        <div class="or-divider">or do it by hand</div>
        <ol class="mini-steps">
            <li>
                <span>
                    <button type="button" class="btn btn-ghost btn-sm" @click=${this.handleDownloadFile}>
                        ${downloadIcon}
                        Download KoboRoot.tgz
                    </button>
                </span>
            </li>
            <li><span>Plug in your Kobo and open the <strong>KOBOeReader</strong> drive.</span></li>
            <li>
                <span>
                    Copy the file into the <code>.kobo</code> folder — press
                    <kbd>⌘</kbd><kbd>⇧</kbd><kbd>.</kbd> to show hidden folders if you're on a Mac.
                </span>
            </li>
        </ol>
        <div class="step-hint">
            ${clock}
            Unplug when the copy finishes — the Kobo restarts by itself.
        </div>
    `
}
